import React from 'react';
import {ICONS} from './SupervisorAgentTrace';
import type {LiveTraceState, LiveTraceStep} from './supervisorLiveTrace';
import {
  TraceWrap,
  TraceStepRow,
  StepIcon,
  StepBody,
  StepLabel,
  StepSub,
  StepStatus,
  Spinner,
  CheckIcon,
  FailIcon,
  FailMeta,
  NestedGroup,
  GroupLabel,
  SourcesRow,
  SourcesLabel,
  SourcePill,
} from './SupervisorAgentTrace.style';

const CheckSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.6}>
    <path d='M5 13l4 4L19 7' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const FailSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.6}>
    <path d='M6 6l12 12M18 6L6 18' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const StepRow: React.FC<{step: LiveTraceStep}> = ({step}) => (
  <TraceStepRow $icon={step.icon}>
    <StepIcon $icon={step.icon}>{ICONS[step.icon]}</StepIcon>
    <StepBody>
      <StepLabel>{step.label}</StepLabel>
      {step.sub && <StepSub>{step.sub}</StepSub>}
      <StepStatus>
        {step.status === 'done' ? (
          <>
            <CheckIcon>
              <CheckSvg />
            </CheckIcon>
            <span>{step.resultMeta || 'done'}</span>
          </>
        ) : step.status === 'error' ? (
          <>
            <FailIcon>
              <FailSvg />
            </FailIcon>
            <FailMeta>{step.resultMeta || 'failed'}</FailMeta>
          </>
        ) : (
          <>
            <Spinner />
            <span>{step.resultMeta || 'Running…'}</span>
          </>
        )}
      </StepStatus>
    </StepBody>
  </TraceStepRow>
);

// Buckets steps into contiguous runs sharing the same `group`, so e.g. all
// "Understanding your query" QUO steps land in one nested box while the
// top-level Supervisor/Insights steps render ungrouped, matching the
// mockup's nesting. Relies on `trace.steps` already being in arrival order.
const groupConsecutive = (steps: LiveTraceStep[]) => {
  const chunks: Array<{group?: string; steps: LiveTraceStep[]}> = [];
  steps.forEach((step) => {
    const last = chunks[chunks.length - 1];
    if (last && last.group === step.group) {
      last.steps.push(step);
    } else {
      chunks.push({group: step.group, steps: [step]});
    }
  });
  return chunks;
};

interface LiveAgentTraceProps {
  trace?: LiveTraceState;
}

// Renders the real, SSE-event-driven agent trace (as opposed to
// <SupervisorAgentTrace />'s canned dummy-data replay animation).
export const LiveAgentTrace: React.FC<LiveAgentTraceProps> = ({trace}) => {
  if (!trace || trace.steps.length === 0) return null;
  console.log("Trace",trace);

  return (
    <>
      <TraceWrap>
        {groupConsecutive(trace.steps).map((chunk, i) => {
          const rows = chunk.steps.map((step) => (
            <StepRow
              key={step.key}
              step={step}
            />
          ));
          return chunk.group ? (
            <NestedGroup key={i}>
              <GroupLabel>{chunk.group}</GroupLabel>
              {rows}
            </NestedGroup>
          ) : (
            <React.Fragment key={i}>{rows}</React.Fragment>
          );
        })}
      </TraceWrap>

      {/* {trace.sources.length > 0 && (
        <SourcesRow>
          <SourcesLabel>Data sources</SourcesLabel>
          {trace.sources.map((source) => (
            <SourcePill key={source}>{source}</SourcePill>
          ))}
        </SourcesRow>
      )} */}
    </>
  );
};

export default LiveAgentTrace;
